// src/pages/Login.jsx
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/global.css';

import { login, getAttendantMe, getSession } from '../api/api';
import { hasReadableSessionCookie, setAuthSession, setAuthRoleSnapshot } from '../utils/auth';
import { clearCompanyScopedCaches } from '../utils/cache.js';
import { Eye, EyeOff } from 'lucide-react';

function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function waitForReadableSessionCookie({ timeoutMs = 1200, intervalMs = 100 } = {}) {
    if (hasReadableSessionCookie()) {
      return true;
    }

    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      if (hasReadableSessionCookie()) {
        return true;
      }
    }

    return hasReadableSessionCookie();
  }

  async function handleSubmit(e) {
    e.preventDefault();

    setError(null);
    setLoading(true);
    try {
      const result = await login(email, password); // Responderá com cookies httpOnly; retorna dados mínimos

      if (!result.success) {
        setError('Falha no login. Verifique suas credenciais.');
        return;
      }

      clearCompanyScopedCaches(); // limpa caches legados antes de carregar dados da nova sessão

      // Aguarda a propagação do cookie de sessão antes de consultar /auth/session.
      const hasSessionCookie = await waitForReadableSessionCookie({ timeoutMs: 2500, intervalMs: 100 });
      if (!hasSessionCookie) {
        setError(
          'Não foi possível iniciar a sessão no navegador. Atualize a página e tente novamente.'
        );
        return;
      }

      // Recupera sessão atual do backend (usa cookies)
      const loginAttendantId = result?.attendant_id ?? null;
      const loginCompanyId = result?.company_id ?? null;
      let sessionInfo = null;
      try {
        sessionInfo = await getSession();
      } catch (err) {
        console.error('Erro ao carregar sessão após login:', err);
      }

      const hasSessionInfo = sessionInfo && typeof sessionInfo === 'object';
      const attendant_id = hasSessionInfo ? sessionInfo?.attendant_id ?? null : loginAttendantId;
      const nextCompanyId = hasSessionInfo ? sessionInfo?.company_id ?? null : loginCompanyId;
      let resolvedRoleName = hasSessionInfo ? sessionInfo?.role ?? null : null;
      let resolvedRoleId = hasSessionInfo ? sessionInfo?.role_id ?? null : null;
      let resolvedAttendantId = attendant_id;
      let resolvedCompanyId = nextCompanyId;

      // Perfis vinculados a empresa precisam validar e hidratar a sessão via /attendants/me.
      if (resolvedCompanyId !== null && resolvedCompanyId !== undefined) {
        try {
          const me = await getAttendantMe(resolvedCompanyId);
          resolvedAttendantId = me?.id ?? resolvedAttendantId;
          resolvedCompanyId = me?.company_id ?? resolvedCompanyId;
          resolvedRoleId = me?.role_id ?? me?.role?.id ?? resolvedRoleId;
          resolvedRoleName = me?.role?.name ?? me?.role_name ?? resolvedRoleName;
        } catch (err) {
          console.error('Erro ao carregar /attendants/me após login:', err);
          setError(
            'Não foi possível carregar os dados da empresa após o login. Tente novamente.'
          );
          return;
        }
      }

      // Atualiza snapshot em memória para consumo do restante da app
      setAuthSession({
        attendantId: resolvedAttendantId,
        companyId: resolvedCompanyId,
        roleName: resolvedRoleName,
        roleId: resolvedRoleId,
      });

      setAuthRoleSnapshot(resolvedRoleName || null, resolvedRoleId);

      window.dispatchEvent(
        new CustomEvent('bluezap:auth-changed', {
          detail: {
            attendantId: resolvedAttendantId ?? null,
            companyId: resolvedCompanyId ?? null,
            roleName: resolvedRoleName || null,
            roleId: resolvedRoleId ?? null,
          },
        })
      );

      const normalizedRoleName = String(resolvedRoleName || '').trim().toLowerCase();
      const isSuperAdmin = normalizedRoleName === 'super admin';
      const isAgent = normalizedRoleName === 'agent' || normalizedRoleName === 'agente';
      if (isSuperAdmin && !resolvedCompanyId) {
        navigate('/companyRegister', { replace: true });
        return;
      }

      if (isAgent && !resolvedCompanyId) {
        navigate('/client-updates', { replace: true });
        return;
      }

      if (!isSuperAdmin && !isAgent && !resolvedCompanyId) {
        setError('Seu usuário não possui empresa vinculada. Contate o administrador.');
        return;
      }

      navigate('/dashboard', { replace: true });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={handleSubmit}>
      <img src="/logo.png" alt="Logo BlueZap" className="logo-imagem" />
        <h2>Conecte-se</h2>

        <input
          id="login-email"
          name="username"
          type="email"
          placeholder="E-mail"
          value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="username"
          required
        />

        <div className="password-field">
          <input
            id="login-password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Senha"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(prev => !prev)}
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? (
              <Eye className="password-toggle__icon" aria-hidden="true" />
            ) : (
              <EyeOff className="password-toggle__icon" aria-hidden="true" />
            )}
          </button>
        </div>

        <Link className="auth-form-link auth-form-link--end" to="/forgot-password">
          Esqueci minha senha
        </Link>

        <button className="auth-form-submit" type="submit" disabled={loading}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>

        {error && <p className="auth-form-message auth-form-message--error">{error}</p>}
      </form>
    </div>
  );
}

export default Login;
